import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {LoaderService} from '../../../../services/loader/loader.service';
import {NgClass} from '@angular/common';
import {MetaTitle} from '../../../../decorator/meta-title';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CountryService} from '../../../../services/country/country.service';
import {StatisticsService} from '../../../../services/statistics/statistics.service';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {PercentPipe} from '../../../../pipes/percent/percent.pipe';
import {LangService} from '../../../../services/language/lang.service';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {TransactionListComponent} from './transaction-list/transaction-list.component';
import {TooltipDirective} from '../../../../directives/tooltip/tooltip.directive';
import {SortableTableDirective} from '../../../../directives/sortable-table/sortable-table.directive';
import {SortableHeaderDirective} from '../../../../directives/sortable-table/sortable-header.directive';
import {ActivatedRoute, ParamMap, RouterLink} from '@angular/router';
import {FileDownloadService} from '../../../../services/file-download/file-download.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';
import {StatsFiltersComponent} from '../stats-filters/stats-filters.component';
import {StatsGoalTypes} from '../../../../constants/stats-goal-types';
import {StatsTransactionDates} from '../../../../constants/stats-transaction-dates';
import {StatsFilterUrlService, StatsPeriodParam} from '../../../../services/stats-filter-url/stats-filter-url.service';

const DEFAULT_PERIOD = 'this-month';
const DEFAULT_GROUP_BY = 'date-day';

@Component({
  selector: 'app-detailed',
  imports: [
    DateRangeSelectorComponent,
    StatsFiltersComponent,
    FormsModule,
    ReactiveFormsModule,
    PercentPipe,
    MoneyPipe,
    NgClass,
    TooltipDirective,
    SortableTableDirective,
    SortableHeaderDirective,
    RouterLink,
  ],
  templateUrl: './detailed.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './detailed.component.scss'
})
@MetaTitle('Detailed statistics')
export class DetailedComponent {
  rows: any[] = [];
  totals: any = {};

  loading = true;

  filters: any = {
    groupBy: DEFAULT_GROUP_BY,
    transactionDate: null,
  };

  groupByOptions = [
    {value: 'date-day', label: 'Date - by day'},
    {value: 'date-week', label: 'Date - by week'},
    {value: 'date-month', label: 'Date - by month'},
    {value: 'landingPage', label: 'Promotion links'},
    {value: 'pool', label: 'Pools'},
    {value: 'partner', label: 'Partners'},
    {value: 'offer', label: 'Offers'},
    {value: 'country', label: 'Countries'},
    {value: 'lang', label: 'Languages'},
    {value: 'subid', label: 'SubID'},
    {value: 'tc1', label: 'Tracker 1'},
    {value: 'tc2', label: 'Tracker 2'},
    {value: 'tc3', label: 'Tracker 3'},
    {value: 'tc4', label: 'Tracker 4'},
    {value: 'source', label: 'Sources'},
    {value: 'product', label: 'Brand'},
  ];

  initialParams: ParamMap;
  initialPeriod: StatsPeriodParam;

  currentFilters: any = {};

  moreDetails: any = {
    lead: false,
    firstProposal: false,
    strategy: false,
    doubleAnim: false,
    auto: false,
    loyalty: false,
    opar: false,
    misc: false,
    total: false,
  };

  goalTypeNameList = StatsGoalTypes;
  transactionDates = StatsTransactionDates;

  @ViewChild(DateRangeSelectorComponent, {static: true}) dateSelector!: DateRangeSelectorComponent;
  @ViewChild(StatsFiltersComponent, {static: true}) statsFilters!: StatsFiltersComponent;

  constructor(
    private fileDownload: FileDownloadService,
    private userActivity: UserActivityService,
    private statisticsService: StatisticsService,
    private dialog: DialogService,
    protected loader: LoaderService,
    protected countryService: CountryService,
    protected langService: LangService,
    protected route: ActivatedRoute,
    private urlFilters: StatsFilterUrlService,
  ) {
    this.loader.startLoading();

    this.initialParams = this.urlFilters.getInitialParams(this.route);
    this.initialPeriod = this.urlFilters.getPeriod(this.initialParams, DEFAULT_PERIOD);

    const groupBy = this.initialParams.get('groupBy');
    if (this.groupByOptions.some(option => option.value === groupBy)) {
      this.filters.groupBy = groupBy;
    }

    const transactionDate = this.initialParams.get('transactionDate');
    if (this.transactionDates.some(option => option.value === transactionDate)) {
      this.filters.transactionDate = transactionDate;
    }
  }

  saveFilters(): void {
    this.urlFilters.save(this.route, this.getUrlParams());
    this.filter();
  }

  clearFilters(): void {
    this.urlFilters.clear(this.route);
    this.filters = {groupBy: DEFAULT_GROUP_BY, transactionDate: null};
    this.statsFilters.reset();
    this.urlFilters.resetPeriod(this.dateSelector, DEFAULT_PERIOD);
    this.filter();
  }

  private getUrlParams(): any {
    return {
      ...this.urlFilters.getPeriodParams(this.dateSelector),
      groupBy: this.filters.groupBy,
      transactionDate: this.filters.transactionDate,
      ...this.statsFilters.getCriteria(),
    };
  }

  getGoalValue(row: any, goalName: string, goalTypeName: string): any {
    if (!('goals' in row)) {
      return null;
    }

    if (!(goalName in row['goals'])) {
      return null;
    }

    if (!(goalTypeName in row['goals'][goalName])) {
      return null;
    }

    return row['goals'][goalName][goalTypeName];
  }

  getCheckouts(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'checkout', goalTypeName);
    if (value !== null) {
      return value.count;
    }

    return 0;
  }

  getSales(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'sale', goalTypeName);
    if (value !== null) {
      return value.count;
    }

    return 0;
  }

  getTurnover(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'sale', goalTypeName);
    if (value !== null) {
      return value.amount;
    }

    return 0;
  }

  getSubscriptions(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'subscription', goalTypeName);
    if (value !== null) {
      return value.count;
    }

    return 0;
  }

  getRenewals(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'renewal', goalTypeName);
    if (value !== null) {
      return value.count;
    }

    return 0;
  }

  getRefunds(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'refund', goalTypeName);
    if (value !== null) {
      return value.count;
    }

    return 0;
  }

  getChargebacks(row: any, goalTypeName: string): number {
    const value = this.getGoalValue(row, 'chargeback', goalTypeName);
    if (value !== null) {
      return value.count;
    }

    return 0;
  }

  getLabel(row: any): string {
    if (this.currentFilters.groupBy.startsWith('date-')) {
      return new Date(row.label).toLocaleDateString();
    }

    if (this.currentFilters.groupBy == 'country') {
      const countryName = this.countryService.getName(row.key);
      return countryName ? countryName : 'Unknown';
    }

    if (this.currentFilters.groupBy == 'lang') {
      const lang = this.langService.getName(row.key);
      return lang ? lang : 'Unknown';
    }

    return row.label;
  }

  isGroupedByLandingPage(): boolean {
    return this.currentFilters.groupBy === 'landingPage';
  }

  isGroupedByDay(): boolean {
    return this.currentFilters.groupBy === 'date-day';
  }

  getHourlyQueryParams(row: any): any {
    const {startDate, endDate, groupBy, partnerId, ...criteria} = this.currentFilters;

    return {
      ...criteria,
      ...(partnerId ? {partnerId: [].concat(partnerId).join(',')} : {}),
      startDate: row.key,
      endDate: row.key,
    };
  }

    getLandingPageEditLink(row: any): {route: any[], title: string} | null {
    if (row.landingPage?.funnelId) {
      return {route: ['/promotion-tools/cms/funnels', row.landingPage.funnelId], title: 'Edit funnel'};
    }

    if (row.landingPage?.staticPageId) {
      return {route: ['/promotion-tools/cms/static-pages', row.landingPage.staticPageId], title: 'Edit static page'};
    }

    if (row.key) {
      return {route: ['/promotion-tools/landing-pages', row.key], title: 'Edit promotion link'};
    }

    return null;
  }

  getGroupByColumnName(): string {
    switch (this.currentFilters.groupBy) {
      case 'landingPage':
        return 'Promotion links';
      case 'pool':
        return 'Pools';
      case 'partner':
        return 'Partners';
      case 'offer':
        return 'Offers';
      case 'subid':
        return 'SubID';
      case 'tc1':
        return 'Tracker 1';
      case 'tc2':
        return 'Tracker 2';
      case 'tc3':
        return 'Tracker 3';
      case 'tc4':
        return 'Tracker 4';
      case 'country':
        return 'Countries';
      case 'lang':
        return 'Languages';
      case 'source':
        return 'Sources';
    }

    return 'Date';
  }

  hasGoalType(goalTypeName: string): boolean {
    if (!this.currentFilters.goalTypeNames) {
      return true;
    }

    return !!this.currentFilters.goalTypeNames?.includes(goalTypeName);
  }

  hasCost(): boolean {
    if (['country', 'lang'].includes(this.currentFilters.groupBy)) {
      return false;
    }

    if (this.currentFilters.lang || this.currentFilters.country) {
      return false;
    }

    return true;
  }

  filter(): void {
    this.loading = true;

    this.currentFilters = {
      startDate: this.dateSelector.getDateISO8601From(),
      endDate: this.dateSelector.getDateISO8601To(),
      groupBy: this.filters.groupBy,
      ...this.statsFilters.getCriteria(),
    };

    if (this.filters.transactionDate) {
      this.currentFilters.transactionDate = this.filters.transactionDate;
    }

    this.loader.startLoading();
    this.urlFilters.update(this.route, this.getUrlParams());
    this.userActivity.reportFilters(this.currentFilters);
    this.statisticsService.getDetailed(this.currentFilters).subscribe((data: any) => {
      this.rows = data.rows;
      this.totals = data.totals;
      this.loader.stopLoading();
      this.loading = false;
    });
  }

  showTransactions(row: any, goalName: string, goalTypeName: string): void {
    this.dialog.openDialog(TransactionListComponent, {
      data: {
        key: row.key,
        filters: this.currentFilters,
        goalName,
        goalTypeName
      },
      class: 'full-width'
    }).subscribe(() => {
    });
  }

  getARTooltip(row: any): string {
    const ar = row.leadAR - row.leadReactivated;
    return 'AR: ' + ar + ' - Reactivated: ' + row.leadReactivated;
  }

  downloadCsv(): void {
    if (this.loading || !this.rows.length) {
      return;
    }

    const goalTypes = this.goalTypeNameList.filter(goalType => this.hasGoalType(goalType.key));
    goalTypes.push({key: 'totals', label: 'Total'});

    const headers = [
      this.getGroupByColumnName(),
      'Clicks',
      'Cost',
      'EPC / CPC',
      'CPL',
      'Lead OK',
      'Lead OK (%)',
      'Lead KO',
      'Lead AR',
      'Lead AR (%)',
      'Lead DOI',
      'Lead DOI (%)',
      'Lead P0',
      'Lead P0 (%)',
      'Lead P2',
      'Lead P2 (%)',
      'Lead AA',
      'Lead AA (%)',
      'Lead Xsell',
      'Lead Xsell (%)',
    ];

    for (const goalType of goalTypes) {
      headers.push(
        `${goalType.label} Checkout`,
        `${goalType.label} Sales`,
        `${goalType.label} Turnover`,
        `${goalType.label} Subscription`,
        `${goalType.label} Renewal`,
        `${goalType.label} Refund`,
        `${goalType.label} Chargeback`,
      );
    }

    const csvRows: string[] = [headers.map(header => this.csvCell(header)).join(',')];

    for (const row of this.rows) {
      csvRows.push(this.buildDetailedCsvRow(row, this.getLabel(row), goalTypes));
    }

    csvRows.push(this.buildDetailedCsvRow(this.totals, 'Total', goalTypes));

    this.triggerCsvDownload(csvRows.join('\n'), 'detailed-statistics.csv');
  }

  private buildDetailedCsvRow(row: any, label: string, goalTypes: {key: string, label: string}[]): string {
    const cells: any[] = [
      this.csvCell(label),
      row.clicks ?? 0,
      this.formatMoney(row.cost),
      this.formatMoney(row.cpc),
      this.formatMoney(row.cpl),
      row.leadP0 ?? 0,
      this.formatPercent(row.leadP0Percent),
      row.leadOk ?? 0,
      this.formatPercent(row.leadOkPercent),
      row.leadKo ?? 0,
      row.leadAR ?? 0,
      this.formatPercent(row.leadARPercent),
      row.leadDoi ?? 0,
      this.formatPercent(row.leadDoiPercent),
      row.leadCoregOk ?? 0,
      this.formatPercent(row.leadCoregOkPercent),
      row.leadCoregAAOk ?? 0,
      this.formatPercent(row.leadCoregAAOkPercent),
      row.leadCrossOk ?? 0,
      this.formatPercent(row.leadCrossOkPercent),
    ];

    for (const goalType of goalTypes) {
      cells.push(
        this.getCheckouts(row, goalType.key),
        this.getSales(row, goalType.key),
        this.formatMoney(this.getTurnover(row, goalType.key)),
        this.getSubscriptions(row, goalType.key),
        this.getRenewals(row, goalType.key),
        this.getRefunds(row, goalType.key),
        this.getChargebacks(row, goalType.key),
      );
    }

    return cells.join(',');
  }

  private formatMoney(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '0.00' : (input / 100).toFixed(2);
  }

  private formatPercent(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '0.00' : (input * 100).toFixed(2);
  }

  private csvCell(value: any): string {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  }

  private triggerCsvDownload(content: string, filename: string): void {
    this.fileDownload.saveCsv(content, filename);
  }
}
